import { useEffect, useMemo, useState } from "react";
import { API, formatINR } from "@/lib/api";
import { ArrowUpDown, Search, Download, FileSpreadsheet, Loader2, X } from "lucide-react";

const PAGE = 400;

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const Cell = ({ col, value }) => {
  if (col.type === "currency")
    return (
      <span className={`font-mono text-[12.5px] ${col.highlight && value ? "text-red-600" : ""} ${col.bold ? "font-semibold text-slate-900" : ""}`}>
        {value ? formatINR(value) : "-"}
      </span>
    );
  if (col.type === "number")
    return <span className={`font-mono text-[12.5px] ${col.highlight && value ? "text-red-600" : ""}`}>{(value ?? 0).toLocaleString("en-IN")}</span>;
  if (col.type === "mono") return <span className="font-mono text-[12px] text-slate-500">{value}</span>;
  if (col.type === "date") return <span className="text-slate-500">{fmtDate(value)}</span>;
  if (col.type === "badge")
    return (
      <span className={`inline-block text-[10.5px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${col.map[value] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
        {value}
      </span>
    );
  return <span className={col.bold ? "font-semibold text-slate-900" : ""}>{value}</span>;
};

export default function ReportTable({ reportKey, columns, rows, totals, initialQuery = "", onRowClick }) {
  const [query, setQuery] = useState(initialQuery);
  const [sort, setSort] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => { setLimit(PAGE); }, [rows, query]);

  const filtered = useMemo(() => {
    let out = rows;
    if (query) {
      const q = query.toLowerCase();
      out = rows.filter((r) => columns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(q)));
    }
    if (sort) {
      out = [...out].sort((a, b) => {
        const av = a[sort.key] ?? "", bv = b[sort.key] ?? "";
        const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, query, sort, columns]);

  const exportCsv = () => {
    const head = columns.map((c) => c.label).join(",");
    const body = filtered
      .map((r) => columns.map((c) => `"${String(r[c.key] ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([head + "\n" + body], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${reportKey}.csv`;
    a.click();
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/reports/${reportKey}/export`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `DeePiping_${reportKey}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setExporting(false);
    }
  };

  const numericTotals = columns.filter((c) => c.type === "currency" && totals && totals[c.key] !== undefined);
  const displayTotals = useMemo(() => {
    if (!query || !totals) return totals;
    const t = {};
    numericTotals.forEach((c) => {
      t[c.key] = filtered.reduce((s, r) => s + (r[c.key] || 0), 0);
    });
    return t;
  }, [query, filtered, totals]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden fade-up">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid={`${reportKey}-search-input`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search rows…"
            className="w-full bg-white border border-slate-300 rounded-md pl-9 pr-8 py-1.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-[#B01E24]/50"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid={`${reportKey}-clear-search-button`}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-800"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500" data-testid={`${reportKey}-row-count`}>{filtered.length} rows</span>
          <button
            onClick={exportCsv}
            data-testid={`${reportKey}-export-csv-button`}
            className="flex items-center gap-1.5 text-xs bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-md px-3 py-1.5 transition-colors"
          >
            <Download size={12} /> CSV
          </button>
          <button
            onClick={exportExcel}
            disabled={exporting}
            data-testid={`${reportKey}-export-excel-button`}
            className="flex items-center gap-1.5 text-xs bg-emerald-700/80 hover:bg-emerald-600 disabled:opacity-60 text-white border border-emerald-600/50 rounded-md px-3 py-1.5 transition-colors"
          >
            {exporting ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} Excel
          </button>
        </div>
      </div>
      <div className="overflow-x-auto report-scroll">
        <table className="w-full text-[13px]" data-testid={`${reportKey}-table`}>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {columns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => setSort((s) => ({ key: c.key, dir: s?.key === c.key && s.dir === "desc" ? "asc" : "desc" }))}
                  className="text-left px-3.5 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-800 whitespace-nowrap select-none"
                >
                  <span className="inline-flex items-center gap-1">{c.label}<ArrowUpDown size={9} className="opacity-50" /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, limit).map((r, i) => (
              <tr
                key={i}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                data-testid={onRowClick ? `${reportKey}-drilldown-row-${i}` : undefined}
                title={onRowClick ? "Open ledger for this party" : undefined}
                className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${onRowClick ? "cursor-pointer hover:bg-[#B01E24]/[0.04]" : ""}`}
              >
                {columns.map((c) => (
                  <td key={c.key} className="px-3.5 py-2 whitespace-nowrap text-slate-700">
                    <Cell col={c} value={r[c.key]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {numericTotals.length > 0 && (
            <tfoot>
              <tr className="bg-slate-50 border-t border-slate-200">
                {columns.map((c, idx) => (
                  <td key={c.key} className="px-3.5 py-2.5 font-mono text-[12px] font-semibold text-slate-900 whitespace-nowrap">
                    {idx === 0 ? (query ? "TOTAL (filtered)" : "TOTAL") : displayTotals[c.key] !== undefined && c.type === "currency" ? formatINR(displayTotals[c.key]) : ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {filtered.length > limit && (
        <div className="flex items-center justify-center gap-3 px-4 py-2.5 border-t border-slate-200 bg-slate-50/60">
          <span className="text-xs text-slate-500">Showing {limit.toLocaleString("en-IN")} of {filtered.length.toLocaleString("en-IN")} rows</span>
          <button onClick={() => setLimit((l) => l + 1000)} data-testid={`${reportKey}-show-more-button`}
            className="text-xs font-medium text-[#B01E24] border border-[#B01E24]/30 rounded-full px-3.5 py-1 hover:bg-[#B01E24]/5 transition-colors">
            Show 1,000 more
          </button>
        </div>
      )}
    </div>
  );
}
